import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Theme controller for the interview experience. `mode` is dark | light | auto and usually comes
 * from the interview config (admin-chosen). It toggles the `dark` class on <html> (Tailwind is
 * configured with darkMode: 'class'), so every screen can use `dark:` variants.
 *
 *   <ThemeProvider mode={config.theme_mode}> … </ThemeProvider>
 *   const { resolved, mode, setMode } = useTheme();   // resolved is 'dark' | 'light'
 */
const ThemeContext = createContext({ mode: 'dark', resolved: 'dark', setMode: () => {} });

const prefersDark = () =>
  typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : true;

const applyClass = (resolved) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.style.colorScheme = resolved;
};

export const ThemeProvider = ({ mode: modeProp = 'dark', children }) => {
  const [mode, setMode] = useState(modeProp);
  useEffect(() => { setMode(modeProp); }, [modeProp]);

  const [systemDark, setSystemDark] = useState(prefersDark());
  useEffect(() => {
    if (mode !== 'auto' || typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => setSystemDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [mode]);

  const resolved = mode === 'auto' ? (systemDark ? 'dark' : 'light') : mode;
  useEffect(() => { applyClass(resolved); }, [resolved]);

  const value = useMemo(() => ({ mode, resolved, setMode }), [mode, resolved]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
export default ThemeProvider;
